import { authFetch } from "#app/utils/auth.client";
import {
  ctxHeaders,
  type ProjectsPagination,
  readError,
  threadsBase,
} from "#app/utils/projects/shared";
import { type WorkflowSvcContext } from "#app/utils/workflow-svc";

export type ProjectThread = {
  id: string;
  title: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
};

export type ProjectThreadMessage = {
  id: string;
  role: "user" | "assistant" | "system";
  type: "text" | "operator_run" | "input_request" | "notification";
  content: string;
  metadata: Record<string, unknown> | null;
  createdAt: string;
};

export async function listProjectThreads(
  projectId: string,
  params: {
    page?: number;
    limit?: number;
    orgName?: string;
    teamName?: string;
    teamKey?: string;
  },
): Promise<{ threads: ProjectThread[]; pagination: ProjectsPagination }> {
  const { page = 1, limit = 50, orgName, teamName, teamKey } = params;
  const url = new URL(threadsBase(projectId));
  url.searchParams.set("page", String(page));
  url.searchParams.set("limit", String(limit));

  const response = await authFetch(url.toString(), {
    method: "GET",
    headers: { ...ctxHeaders({ orgName, teamName, teamKey }) },
  });
  if (!response.ok) {
    throw new Error(
      await readError(response, `Failed to load threads (${response.status})`),
    );
  }
  return (await response.json()) as {
    threads: ProjectThread[];
    pagination: ProjectsPagination;
  };
}

export async function createProjectThread(
  projectId: string,
  input: { title?: string },
  ctx?: WorkflowSvcContext,
): Promise<ProjectThread> {
  const response = await authFetch(threadsBase(projectId), {
    method: "POST",
    headers: { "Content-Type": "application/json", ...ctxHeaders(ctx) },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new Error(
      await readError(response, `Failed to create thread (${response.status})`),
    );
  }
  return (await response.json()) as ProjectThread;
}

export async function deleteProjectThread(
  projectId: string,
  threadId: string,
  ctx?: WorkflowSvcContext,
): Promise<void> {
  const response = await authFetch(
    `${threadsBase(projectId)}/${encodeURIComponent(threadId)}`,
    {
      method: "DELETE",
      headers: { ...ctxHeaders(ctx) },
    },
  );
  if (!response.ok) {
    throw new Error(
      await readError(response, `Failed to delete thread (${response.status})`),
    );
  }
}

export async function getProjectThreadMessages(
  projectId: string,
  threadId: string,
  params: {
    limit?: number;
    before?: string;
    after?: string;
    orgName?: string;
    teamName?: string;
    teamKey?: string;
  },
): Promise<{
  thread: ProjectThread;
  messages: ProjectThreadMessage[];
  hasMore: boolean;
}> {
  const { limit = 50, before, after, orgName, teamName, teamKey } = params;
  const url = new URL(
    `${threadsBase(projectId)}/${encodeURIComponent(threadId)}/messages`,
  );
  url.searchParams.set("limit", String(limit));
  if (before) url.searchParams.set("before", before);
  if (after) url.searchParams.set("after", after);

  const response = await authFetch(url.toString(), {
    method: "GET",
    headers: { ...ctxHeaders({ orgName, teamName, teamKey }) },
  });
  if (!response.ok) {
    throw new Error(
      await readError(response, `Failed to load messages (${response.status})`),
    );
  }
  return (await response.json()) as {
    thread: ProjectThread;
    messages: ProjectThreadMessage[];
    hasMore: boolean;
  };
}

export async function autoTitleProjectThread(
  projectId: string,
  threadId: string,
  ctx?: WorkflowSvcContext,
): Promise<string> {
  const response = await authFetch(
    `${threadsBase(projectId)}/${encodeURIComponent(threadId)}/title`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...ctxHeaders(ctx) },
      body: JSON.stringify({ type: "auto" }),
    },
  );
  if (!response.ok) {
    throw new Error(
      await readError(response, `Failed to update title (${response.status})`),
    );
  }
  const json = (await response.json()) as { title: string };
  return json.title;
}
export type ChatAttachment = { name: string; url: string };

/**
 * POST /:threadId/attachments — upload files for a run; returns their URLs so
 * they can be passed as file inputs in the next router message.
 */
export async function uploadProjectChatAttachments(
  projectId: string,
  threadId: string,
  files: File[],
  ctx?: WorkflowSvcContext,
): Promise<ChatAttachment[]> {
  const form = new FormData();
  for (const file of files) form.append("files", file);
  const url = `${threadsBase(projectId)}/${encodeURIComponent(threadId)}/attachments`;
  const response = await authFetch(url, {
    method: "POST",
    headers: { ...ctxHeaders(ctx) },
    body: form,
  });
  if (!response.ok) {
    throw new Error(
      await readError(response, `Failed to upload files (${response.status})`),
    );
  }
  const json = (await response.json()) as { attachments: ChatAttachment[] };
  return json.attachments;
}
